import {
  Box,
  Divider,
  Flex,
  Grid,
  GridItem,
  Icon,
  Stack,
  Tag,
  Badge,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  FaEnvelope,
  FaPhone,
  FaUserCircle,
} from "react-icons/fa";
import { TbFileDescription, TbTicket, TbCreditCard } from "react-icons/tb";
import { formatDateTime } from "utils/dateUtils/dateUtils";

const InfoRow = ({ label, value }: { label: string; value: any }) => (
  <GridItem>
    <Text fontSize="xs" color="gray.500">
      {label}
    </Text>
    <Text fontWeight={500} wordBreak="break-word">
      {value === undefined || value === null || value === "" ? "N/A" : value}
    </Text>
  </GridItem>
);

const statusColor = (status: string) => {
  switch (status) {
    case "SUCCESSFUL":
    case "REFUNDED":
    case "PAYMENT_VERIFIED":
    case "ACTIVE":
    case "captured":
      return "green";
    case "PAYMENT_FAILED":
    case "CANCELLED":
    case "failed":
      return "red";
    case "PAYMENT_INITIATED":
    case "CANCEL_INITIATED":
      return "orange";
    default:
      return "blue";
  }
};

const IssueDetails = ({ data }: { data?: any }) => {
  if (!data) {
    return (
      <Text
        fontWeight={500}
        color={"red.400"}
        fontSize={"xl"}
        textAlign={"center"}
      >
        No Data Available
      </Text>
    );
  }

  const transaction = data?.transaction;
  const payment = data?.payment;

  const hasUser = !!transaction?.user_id;

  const complainantName = hasUser ? transaction.user_id.name : transaction?.billingDetails?.name;
  const complainantPhone = hasUser ? transaction.user_id.moNumber : transaction?.billingDetails?.phone;
  const complainantEmail = hasUser ? undefined : transaction?.billingDetails?.email;

  const providerList =
    transaction?.orders?.map((o: any) => o?.providerName).filter(Boolean) || [];
  const providerLabel =
    [...new Set(providerList)].length > 1 ? "Multiple" : providerList[0] || "N/A";

  return (
    <Box>
      {/* ---------- Issue Summary ---------- */}
      <Grid
        gap={2}
        mb={4}
        p={{ base: 2, md: 4 }}
        bg="gray.50"
        templateColumns={{ base: "1fr 1fr", md: "1fr 1fr 1fr" }}
        borderRadius="md"
      >
        <Box fontSize={{ base: "sm", md: "md" }}>
          <Text fontWeight={500}>Issue Status</Text>
          <Tag colorScheme="blue">{data?.status}</Tag>
        </Box>
        <Box fontSize={{ base: "sm", md: "md" }}>
          <Text fontWeight={500}>Category</Text>
          <Tag colorScheme="purple">{data?.category?.label}</Tag>
        </Box>
        <Box fontSize={{ base: "sm", md: "md" }}>
          <Text fontWeight={500}>Sub Category</Text>
          <Tag colorScheme="teal">{data?.sub_category?.value}</Tag>
        </Box>
      </Grid>

      <Stack spacing={4}>
        {/* ---------- Description ---------- */}
        <VStack
          align="start"
          spacing={2}
          p={4}
          bg="gray.50"
          borderRadius="md"
          w="full"
        >
          <Flex align="center" mb={2}>
            <Icon as={TbFileDescription} w={6} h={6} color="blue.400" mr={2} />
            <Text fontSize="lg" fontWeight="semibold">
              Description
            </Text>
          </Flex>
          <Text>
            <Text as={"span"} fontWeight={"500"}>
              Subject:
            </Text>{" "}
            {data?.short_desc}
          </Text>
          <Text>
            <Text as={"span"} fontWeight={"500"}>
              Description:
            </Text>{" "}
            {data?.long_desc}
          </Text>
        </VStack>

        {/* ---------- Complainant (shown once, here only) ---------- */}
        {(complainantName || complainantPhone || complainantEmail) && (
          <VStack
            align="start"
            spacing={2}
            p={4}
            bg="gray.50"
            borderRadius="md"
            w="full"
          >
            <Flex align="center" mb={2}>
              <Icon as={FaUserCircle} w={6} h={6} color="teal.500" mr={2} />
              <Text fontSize="lg" fontWeight="semibold">
                Complainant Information
              </Text>
            </Flex>
            <Text>Name: {complainantName || "N/A"}</Text>
            <Flex align="center">
              <Icon as={FaPhone} w={4} h={4} color="gray.500" mr={2} />
              <Text>{complainantPhone || "N/A"}</Text>
            </Flex>
            {complainantEmail && (
              <Flex align="center">
                <Icon as={FaEnvelope} w={4} h={4} color="gray.500" mr={2} />
                <Text>{complainantEmail}</Text>
              </Flex>
            )}
          </VStack>
        )}

        {/* ---------- Transaction Details (Transaction ID lives here only) ---------- */}
        {transaction && (
          <VStack
            align="start"
            spacing={3}
            p={4}
            bg="gray.50"
            borderRadius="md"
            w="full"
          >
            <Flex align="center" mb={1}>
              <Icon as={TbTicket} w={6} h={6} color="orange.400" mr={2} />
              <Text fontSize="lg" fontWeight="semibold">
                Transaction Details
              </Text>
            </Flex>

            <Grid
              templateColumns={{ base: "1fr 1fr", md: "1fr 1fr 1fr" }}
              gap={4}
              w="full"
            >
              <InfoRow label="Transaction ID" value={transaction?._id} />
              <InfoRow label="Start" value={transaction?.startStation} />
              <InfoRow label="Destination" value={transaction?.destinationStation} />
              <InfoRow label="Providers" value={providerLabel} />
              <InfoRow label="Tickets" value={transaction?.ticketCount ?? 0} />
              <InfoRow label="Total Fare" value={`₹ ${transaction?.totalPrice ?? 0}`} />
              <InfoRow label="Date" value={formatDateTime(transaction?.createdAt, "DD-MM-YYYY")} />
              <InfoRow
                label="Status"
                value={
                  <Tag size="sm" colorScheme={statusColor(transaction?.status)}>
                    {transaction?.status}
                  </Tag>
                }
              />
              <InfoRow label="City Code" value={transaction?.cityCode} />
            </Grid>

            {/* Orders within the transaction */}
            {transaction?.orders?.length > 0 && (
              <Box w="full" mt={2}>
                <Text fontWeight={600} fontSize="sm" color="gray.600" mb={2}>
                  Orders
                </Text>
                <Stack spacing={2}>
                  {transaction.orders.map((order: any) => (
                    <Box
                      key={order._id || order.orderId}
                      borderWidth={1}
                      borderColor="gray.200"
                      bg="white"
                      borderRadius="md"
                      p={3}
                    >
                      <Grid
                        templateColumns={{ base: "1fr 1fr", md: "1fr 1fr 1fr" }}
                        gap={3}
                      >
                        <InfoRow label="Item" value={order?.itemName} />
                        <InfoRow label="Provider" value={order?.providerName} />
                        <InfoRow label="Order ID" value={order?.orderId} />
                        <InfoRow label="From" value={order?.startStationName} />
                        <InfoRow label="To" value={order?.destinationStationName} />
                        <InfoRow
                          label="Price/Ticket"
                          value={`₹ ${order?.pricePerTicket ?? 0}`}
                        />
                        <InfoRow
                          label="Order Status"
                          value={
                            <Badge colorScheme={statusColor(order?.status)}>
                              {order?.status}
                            </Badge>
                          }
                        />
                      </Grid>
                      <Box mt={2}>
                        {order?.tickets?.length > 0 ? (
                          order.tickets.map((t: any) => (
                            <Text key={t.ticketId} fontSize="xs" color="gray.500">
                              Ticket #{t.ticketId} — {t.status} (valid till{" "}
                              {new Date(t.validTill).toLocaleString()})
                            </Text>
                          ))
                        ) : (
                          <Text fontSize="xs" color="gray.500">
                            No tickets
                          </Text>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </VStack>
        )}

        {/* ---------- Payment Details (shown once, here only) ---------- */}
        {payment && (
          <VStack
            align="start"
            spacing={3}
            p={4}
            bg="gray.50"
            borderRadius="md"
            w="full"
          >
            <Flex align="center" mb={1}>
              <Icon as={TbCreditCard} w={6} h={6} color="green.500" mr={2} />
              <Text fontSize="lg" fontWeight="semibold">
                Payment Details
              </Text>
            </Flex>

            <Grid
              templateColumns={{ base: "1fr 1fr", md: "1fr 1fr 1fr" }}
              gap={4}
              w="full"
            >
              <InfoRow label="Payment ID" value={payment?.payment_id} />
              <InfoRow label="Order ID" value={payment?.order_id} />
              <InfoRow
                label="Status"
                value={
                  <Tag size="sm" colorScheme={statusColor(payment?.status)}>
                    {payment?.status}
                  </Tag>
                }
              />
              <InfoRow
                label="Method"
                value={payment?.payment_details?.method?.toUpperCase()}
              />
              <InfoRow
                label="Amount Paid"
                value={`₹ ${(payment?.amount_paid ?? 0) / 100}`}
              />
              <InfoRow
                label="Amount Due"
                value={`₹ ${(payment?.amount_due ?? 0) / 100}`}
              />
              <InfoRow
                label="Refund Amount"
                value={`₹ ${(payment?.refundAmount ?? 0) / 100}`}
              />
              <InfoRow label="Currency" value={payment?.currency} />
              <InfoRow label="Receipt" value={payment?.receipt} />
              <InfoRow
                label="UPI VPA"
                value={payment?.payment_details?.details?.entity?.vpa}
              />
              <InfoRow
                label="UPI Txn ID"
                value={
                  payment?.payment_details?.details?.entity?.acquirer_data
                    ?.upi_transaction_id
                }
              />
              <InfoRow
                label="RRN"
                value={payment?.payment_details?.details?.entity?.acquirer_data?.rrn}
              />
            </Grid>
          </VStack>
        )}
      </Stack>
    </Box>
  );
};

export default IssueDetails;